import {
  
  Grid,
  Typography,
  Box,
  IconButton,
  Alert,
} from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import http from "../../../commonFunctions/httpClient";
import {
  buildImageUrl,
  dateFormat,
  getLocalValue,
  LoacalVariables,
} from "../../../commonFunctions/CommonFunctions";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSSelectField from "../../../Components/Select/WSSelect";
import WSPhone from "../../../Components/Phone/WSPhone";
import WSDateField from "../../../Components/DateField/WSDateField";
import clientconfig from "../../../Config/clientConfig.json";
import config from "../../../Config/config.json";
import CloseIcon from "@mui/icons-material/Close";

interface DocumentPreview {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
}

interface PrescriptionProps {
  control?: Control<any>;
  errors?: FieldErrors;
  register: UseFormRegister<any>;
  id: any;
  reset: UseFormReset<any>;
  noteId?: string;
  setValue: UseFormSetValue<any>;
  onCancel: () => void;
  onSuccess?: () => void;
}

const TechnicianDrawer: React.FC<PrescriptionProps> = ({
  control,
  id,
  register,
  reset,
  setValue,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<DocumentPreview[]>(
    [],
  );
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [technicianId, setTechnicianId] = useState("");
  const [dataLoaded, setDataLoaded] = useState(true);
  // ✅ NEW: File validation states
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const profileImageRef = useRef<HTMLInputElement>(null);
  const hasLoadedRef = useRef(false);

  const formValues = useWatch({
    control,
  }) as any;

  // ✅ NEW: Validate file sizes (1MB = 1024 * 1024 bytes)
  const validateFileSizes = (): boolean => {
    const MAX_SIZE = 1024 * 1024; // 1MB

    // Check profile image
    const profileFile = profileImageRef.current?.files?.[0];
    if (profileFile && profileFile.size > MAX_SIZE) {
      setFileSizeError(
        `Profile image must be less than 1MB. Current size: ${(profileFile.size / 1024 / 1024).toFixed(2)}MB`,
      );
      return false;
    }

    // Check documents
    for (const file of documents) {
      if (file.size > MAX_SIZE) {
        setFileSizeError(
          `Document "${file.name}" must be less than 1MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
        );
        return false;
      }
    }

    return true;
  };

  // Get vendor ID
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setVendorId(userId || "");
  }, []);

  // Main ADD/EDIT logic
  useEffect(() => {
    const editMode = id && id.toString().startsWith("technicianEdit-");

    // Reset ALL state when drawer opens
    setIsEditMode(editMode);
    setTechnicianId("");
    setExistingDocuments([]);
    setProfileImagePreview(null);
    setDocuments([]);
    setFileSizeError(null); // ✅ Clear errors

    setDataLoaded(!editMode);
    hasLoadedRef.current = false;

    if (editMode && control) {
      const techId = id.toString().replace("technicianEdit-", "");
      setTechnicianId(techId);
      loadTechnicianData(techId);
    }
  }, [id, control]);

  // 🔥 FIXED: Load data with camelCase matching API response
  const loadTechnicianData = useCallback(
    async (techId: string) => {
      if (hasLoadedRef.current || loading) return;

      try {
        hasLoadedRef.current = true;
        setLoading(true);
        console.log("📡 Fetching technician:", techId);

        const response: any = await http.get(`lab/Technician/${techId}`);

        const data = response?.data || response;

        // 🔥 FIX 2: Reset form with correct camelCase field names
        if (reset) {
          reset({
            name: "",
            phoneNumber: "",
            fullAddress: "",
            dateOfBirth: "",
            qualification: "",
            experience: "",
            workingHours: "",
            "documents.0": "",
            "documents.1": "",
            "documents.2": "",
            "documents.3": "",
          });
        }

        // 🔥 FIX 3: Populate with camelCase (NO setTimeout)
        if (setValue && data) {
          setValue("name", data.name || "");
          setValue("phoneNumber", data.phoneNumber || "");
          setValue("fullAddress", data.fullAddress || "");
          setValue(
            "dateOfBirth",
            dateFormat(data?.dateOfBirth, "DD-MM-YYYY"),
          );
          setValue("qualification", data.qualification || "");
          setValue("experience", data.experience?.toString() || "0");
          setValue("workingHours", data.workingHours || "");

          const booleanToString = (value: any): string => {
            return value === true || value === "true" ? "yes" : "no";
          };

          setValue("documents.0", booleanToString(data.isAadharVerified));
          setValue("documents.1", booleanToString(data.isLicenseVerified));
          setValue(
            "documents.2",
            booleanToString(data.isCertificationVerified),
          );
          setValue("documents.3", booleanToString(data.isPoliceVerified));

          console.log("✅ FORM POPULATED WITH:", data.name, data.phoneNumber);
        }

        // Profile image — try all common field names the API might return
        const profileImgPath =
          data.profileImageUrl ||
          data.profileImage ||
          data.profileImagePath ||
          data.profilePicture ||
          data.imageUrl ||
          data.image ||
          "";
        if (profileImgPath) {
          setProfileImagePreview(buildImageUrl(profileImgPath, config.imgUrl));
        } else {
          setProfileImagePreview(null);
        }

        // Documents — try multiple field names for each doc's URL
        const rawDocs = data.documents || data.technicianDocuments || data.docs || [];
        if (Array.isArray(rawDocs) && rawDocs.length > 0) {
          const docPreviews: DocumentPreview[] = rawDocs.map(
            (doc: any, index: number) => {
              const docUrl =
                doc.documentUrl ||
                doc.url ||
                doc.fileUrl ||
                doc.filePath ||
                doc.documentPath ||
                doc.imageUrl ||
                "";
              return {
                id: doc.id || String(index),
                name: doc.documentType
                  ? `${doc.documentType.toLowerCase()}.pdf`
                  : `document_${index + 1}.pdf`,
                type: doc.documentType || doc.type || "Document",
                url: docUrl ? buildImageUrl(docUrl, config.imgUrl) : "",
                size: 0,
              };
            },
          );
          setExistingDocuments(docPreviews);
        } else {
          setExistingDocuments([]);
        }

        setDataLoaded(true);
      } catch (error: any) {
        console.error("💥 LOAD ERROR:", error);
      } finally {
        setLoading(false);
      }
    },
    [setValue, reset, loading],
  );

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // ✅ Check file size on upload (1MB)
      if (file.size > 1024 * 1024) {
        setFileSizeError(
          `Profile image "${file.name}" must be less than 1MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
        );
        e.target.value = "";
        return;
      }
      setFileSizeError(null);
      const previewUrl = URL.createObjectURL(file);
      setProfileImagePreview(previewUrl);
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);

    // ✅ Check file sizes on upload
    for (const file of newFiles) {
      if (file.size > 1024 * 1024) {
        setFileSizeError(
          `Document "${file.name}" must be less than 1MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
        );
        e.target.value = "";
        return;
      }
    }

    setFileSizeError(null);
    setDocuments((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  const removeDocument = (indexToRemove: number) => {
    setDocuments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removeExistingDocument = (docId: string) => {
    setExistingDocuments((prev) => prev.filter((doc) => doc.id !== docId));
  };

  const handleProfileImageClick = () => {
    profileImageRef.current?.click();
  };

  const removeProfileImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProfileImagePreview(null);
    if (profileImageRef.current) {
      profileImageRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading || (isEditMode && !dataLoaded)) return;

    if (!validateFileSizes()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setFileSizeError(null);
    try {
      const formData = new FormData();

      const profileFile = profileImageRef.current?.files?.[0];
      if (profileFile) formData.append("ProfileImage", profileFile);

      formData.append("LabId", vendorId);
      formData.append("Name", formValues?.name || "");
      formData.append("PhoneNumber", formValues?.phoneNumber || "");
      formData.append("FullAddress", formValues?.fullAddress || "");

      // Convert DD-MM-YYYY (stored by WSDateField) to ISO date-time required by API
      const rawDate = formValues?.dateOfBirth || "";
      let isoDate = "";
      if (rawDate) {
        const parts = rawDate.split("-");
        if (parts.length === 3 && parts[2]?.length === 4) {
          // DD-MM-YYYY → YYYY-MM-DD
          isoDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00.000Z`).toISOString();
        } else {
          isoDate = rawDate;
        }
      }
      formData.append("DateOfBirth", isoDate);

      formData.append("Qualification", formValues?.qualification || "");
      formData.append("Experience", String(parseInt(formValues?.experience || "0", 10) || 0));
      formData.append("WorkingHours", formValues?.workingHours || "");

      documents.forEach((file) => formData.append("Documents", file));

      const documentTypes = ["Aadhar", "License", "Certification", "Police"];
      documentTypes.forEach((type) => formData.append("DocumentTypes", type));

      // Verification flags — backend expects these; without them the dropdowns never persist
      const strToBool = (v: any) => (v === "yes" || v === "true" || v === true ? "true" : "false");
      formData.append("IsAadharVerified", strToBool(formValues?.["documents.0"]));
      formData.append("IsLicenseVerified", strToBool(formValues?.["documents.1"]));
      formData.append("IsCertificationVerified", strToBool(formValues?.["documents.2"]));
      formData.append("IsPoliceVerified", strToBool(formValues?.["documents.3"]));

      let response: any;
      if (isEditMode && technicianId) {
        response = await http.putFormData(`lab/Technician/${technicianId}`, formData);
      } else {
        response = await http.postFormData(`lab/Technician`, formData);
      }

      // putFormData/postFormData swallow HTTP errors and return the error body,
      // so we must verify success here instead of relying on the catch block.
      // This endpoint returns the saved entity (with an `id`) on success rather
      // than a { success: true } wrapper, so accept either shape.
      const isSuccess =
        response?.success === true ||
        (!!response?.id && response?.success !== false && !response?.errors);
      if (!isSuccess) {
        const errMsg =
          response?.message ||
          (response?.errors ? JSON.stringify(response.errors) : null) ||
          (isEditMode ? "Failed to update technician" : "Failed to create technician");
        console.error("❌ Save failed:", response);
        alert(errMsg);
        return;
      }

      const message = response?.message || (isEditMode ? "Technician updated!" : "Technician created!");
      alert(message);

      setDocuments([]);
      setProfileImagePreview(null);
      setExistingDocuments([]);
      if (profileImageRef.current) profileImageRef.current.value = "";
      if (reset) reset();
      onCancel();
      onSuccess?.();
    } catch (error: any) {
      console.error("Submit error:", error);
      alert(error?.response?.data?.message || "Failed to save technician");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (
        profileImagePreview &&
        !profileImagePreview.startsWith(config.imgUrl)
      ) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [profileImagePreview]);

  return (
    <Grid>
      <form id={id} onSubmit={handleSubmit}>
        <Grid container spacing={2} mt={2}>
          {/* 🔥 NEW: File Size Error Display */}
          {fileSizeError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {fileSizeError}
              </Alert>
            </Grid>
          )}

          {/* Profile + Documents */}
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    border: "3px dashed #1976d2",
                    borderRadius: "50%",
                    width: 160,
                    height: 160,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8faff",
                    cursor: "pointer",
                    position: "relative",
                    "&:hover": {
                      backgroundColor: profileImagePreview
                        ? "#e3f2fd"
                        : "#e3f2fd",
                      borderColor: "#1565c0",
                    },
                  }}
                  onClick={handleProfileImageClick}
                >
                  {profileImagePreview ? (
                    <>
                      <Box
                        sx={{
                          width: "80%",
                          height: "80%",
                          borderRadius: "50%",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <img
                          src={profileImagePreview}
                          alt="Profile preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            borderRadius: "50%",
                          }}
                        />
                      </Box>
                      <IconButton
                        onClick={removeProfileImage}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "rgba(0,0,0,0.8)",
                          color: "white",
                          width: 32,
                          height: 32,
                          minWidth: 0,
                          "&:hover": { backgroundColor: "rgba(0,0,0,1)" },
                        }}
                        size="small"
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          backgroundColor: "#e3f2fd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      ></Box>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ textAlign: "center", px: 1 }}
                      >
                        Click to upload
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        (JPG, PNG <strong>&lt; 1MB</strong>)
                      </Typography>
                    </>
                  )}
                </Box>
                <input
                  ref={profileImageRef}
                  type="file"
                  name="profileImage"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={loading}
                  onChange={handleProfileImageChange}
                />
              </Box>
            </Grid>

            {/* Documents Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                📎 Documents ({existingDocuments.length + documents.length})
              </Typography>

              {existingDocuments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ fontWeight: 500 }}
                  >
                    Existing ({existingDocuments.length})
                  </Typography>
                  {existingDocuments.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        bgcolor: "#e8f5e8",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ flexGrow: 1, mr: 1 }}>
                        {doc.type}
                      </Typography>
                      <Box
                        component="img"
                        src={doc.url}
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 1,
                          mr: 1,
                          objectFit: "cover",
                          backgroundColor: "#f0f0f0",
                        }}
                      />
                      <span style={{ fontSize: "20px" }}>✓</span>
                      {isEditMode && (
                        <IconButton
                          size="small"
                          onClick={() => removeExistingDocument(doc.id)}
                          sx={{ p: 0.2 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              <Box
                sx={{
                  position: "relative",
                  border: "2px dashed #1976d2",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#f8faff",
                  "&:hover": { backgroundColor: "#e3f2fd" },
                }}
              >
                <input
                  id="documents-upload"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleDocumentUpload}
                  style={{ display: "none" }}
                  disabled={loading}
                />
                <label htmlFor="documents-upload">
                  <Typography variant="body1" color="primary">
                    ➕{" "}
                    {documents.length === 0
                      ? "Upload New Documents "
                      : `${documents.length} New Files`}
                  </Typography>
                </label>
              </Box>

              {documents.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 120, overflow: "auto" }}>
                  {documents.map((file, index) => (
                    <Box
                      key={`new-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        bgcolor: "#e3f2fd",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ flexGrow: 1, mr: 1 }}>
                        {file.name.length > 20
                          ? `${file.name.substring(0, 20)}...`
                          : file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mr: 1 }}
                      >
                        {Math.round(file.size / 1024)}KB
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeDocument(index)}
                        sx={{ p: 0.2 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* 🔥 ALL FORM FIELDS - FIXED camelCase names */}
          <Grid item xs={12}>
            <WSTextField
              control={control}
              id="name"
              name="name"
              rules={{ required: true }}
              label="Name *"
            />
          </Grid>

          <Grid item xs={12}>
            <WSPhone
              label="Phone Number *"
              rules={{ required: true }}
              control={control}
              id="phoneNumber"
              {...register("phoneNumber")}
              name="phoneNumber"
            />
          </Grid>

          <Grid item xs={12}>
            <WSTextField
              control={control}
              id="fullAddress"
              name="fullAddress"
              rules={{ required: true }}
              label="Full Address *"
            />
          </Grid>

          <Grid item xs={12}>
            <WSDateField
              id="dateOfBirth"
              name="dateOfBirth"
              label="Date of Birth *"
              dateFormat="DD-MM-YYYY"
              control={control}
              isInGridFilter={false}
              rules={{ required: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <WSTextField
              control={control}
              id="qualification"
              name="qualification"
              rules={{ required: true }}
              label="Qualification *"
            />
          </Grid>

          <Grid item xs={12}>
            <WSTextField
              control={control}
              id="experience"
              name="experience"
              rules={{ required: true }}
              label="Experience (Years) *"
            />
          </Grid>

          <Grid item xs={12}>
            <WSTextField
              control={control}
              id="workingHours"
              name="workingHours"
              rules={{ required: true }}
              label="Working Hours *"
            />
          </Grid>

          {/* Verification Fields */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Documents & Verification
            </Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            <Typography variant="body2" color="GrayText">
              Aadhar
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <WSSelectField
              rules={{ required: true }}
              {...register("documents.0")}
              id="aadhar"
              control={control}
              isInGridFilter={false}
              label=""
              name="documents.0"
              options={clientconfig.yesorno.staticOptionData}
            />
          </Grid>
          <Grid item xs={12} sm={10}>
            <Typography variant="body2" color="GrayText">
              License
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <WSSelectField
              rules={{ required: true }}
              {...register("documents.1")}
              id="license"
              control={control}
              label=""
              name="documents.1"
              options={clientconfig.yesorno.staticOptionData}
            />
          </Grid>
          <Grid item xs={12} sm={10}>
            <Typography variant="body2" color="GrayText">
              Certification
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <WSSelectField
              rules={{ required: true }}
              {...register("documents.2")}
              id="certification"
              control={control}
              label=""
              name="documents.2"
              options={clientconfig.yesorno.staticOptionData}
            />
          </Grid>
          <Grid item xs={12} sm={10}>
            <Typography variant="body2" color="GrayText">
              Police Verification
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <WSSelectField
              rules={{ required: true }}
              {...register("documents.3")}
              id="policeVerification"
              control={control}
              label=""
              name="documents.3"
              options={clientconfig.yesorno.staticOptionData}
            />
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default TechnicianDrawer;
